/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Jan 25 2024
 **/

import { ModalID } from "@/frameworks/core/commons";
import { DocumentViewer } from "@/frameworks/core/components";
import { useConfirm, useFileManager } from "@/frameworks/core/hooks";
import { useModal } from "@/frameworks/designStuff/components";
import { useProperties } from "@/frameworks/libraries/properties";
import { downloadBlobData } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import useDeleteFile from "./useDeleteFile";

export default function useFileSelect(file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number]) {
	const modal = useModal();
	const { setCurrentFolder, downloadFiles, downloadFolder } = useFileManager();
	const properties = useProperties();
	const { confirm } = useConfirm();
	const deleteFileOrFolder = useDeleteFile();

	const handleSelectFile = () => {
		switch (file.fileBoxType) {
			case "FILE": {
				showPreview();
				break;
			}
			case "FOLDER": {
				setCurrentFolder((prev) => ({ ...prev, id: file.id, name: file.name || "", parent: prev }));
				break;
			}
			case "LINK": {
				window.open(file.link, "_blank");
				break;
			}
		}
	};

	const downloadFile = () => {
		downloadFiles({ id: file.id }).then((response) => {
			downloadBlobData(response as Blob, file.name || "");
		});
	};

	const downloadFolderZip = () => {
		downloadFolder({ id: file.id }).then((response) => {
			const folderName = `${file.name}.zip` || "";
			const blob = new Blob([response]);
			downloadBlobData(blob, folderName);
		});
	};

	const closePreview = () => {
		modal.hide(ModalID.DOCUMENT_VIEWER);
	};

	const showFileDeleteConfirm = () => {
		confirm(_T("해당 파일을 삭제하시겠습니까?"), {
			onConfirm() {
				deleteFileOrFolder({ id: file.id, fileBoxType: file.fileBoxType });
				closePreview();
			}
		});
	};

	const showPreview = () => {
		modal.show({
			id: ModalID.DOCUMENT_VIEWER,
			content: (
				<DocumentViewer
					createByID={file.createBy || ""}
					fileID={[file.id]}
					fileName={[String(file.name)]}
					mimeType={file.fileMimeType || ""}
					imageList={[`${properties.default.webDomain}${file.filePath}`]}
					onClose={closePreview}
					onRemove={showFileDeleteConfirm}
					onDownload={downloadFile}
					fileDisplaySize={[String(file.fileDisplaySize)]}
					date={file.createDateTime}
				/>
			)
		});
	};

	return { handleSelectFile, downloadFile, downloadFolderZip };
}
